import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  downloadBlobFile,
  downloadTextFile,
  getDefaultReportPeriod,
  getReportErrorMessage,
  ReportEmptyState,
  ReportErrorState,
  ReportPeriod,
  ReportPeriodFilter,
  ReportsNavigation,
  toApiPeriodParams,
} from '../../components/reports/ReportFilters';
import reportsService, { ProofOfPlayRecord } from '../../services/reportsService';

const size = 25;

const ProofOfPlayPage: React.FC = () => {
  const [data, setData] = useState<ProofOfPlayRecord[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [period, setPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [appliedPeriod, setAppliedPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reportsService.getProofOfPlay({
        page: page + 1,
        size,
        ...toApiPeriodParams(appliedPeriod),
      });
      setData(response.items);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod, page]);

  useEffect(() => {
    load();
  }, [load]);

  const applyPeriod = () => {
    setPage(0);
    setAppliedPeriod(period);
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      const csv = await reportsService.exportProofOfPlay(toApiPeriodParams(appliedPeriod));
      downloadTextFile(csv, `proof-of-play-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.csv`);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err, 'Не удалось экспортировать Proof-of-Play'));
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const file = await reportsService.exportReport('proof-of-play', toApiPeriodParams(appliedPeriod), 'xlsx');
      downloadBlobFile(file, `proof-of-play-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.xlsx`);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err, 'Не удалось экспортировать Proof-of-Play в Excel'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Proof-of-Play</Typography>
          <Typography variant="body2" color="text.secondary">
            Подтверждения воспроизведения по устройствам, медиа, плейлистам и расписанию.
          </Typography>
        </Box>

        <ReportsNavigation />

        <ReportPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          onApply={applyPeriod}
          loading={loading}
          extraActions={(
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />} onClick={exportExcel} disabled={loading || exporting || total === 0}>
                Excel
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} disabled={loading || exporting || total === 0}>
                CSV
              </Button>
            </Stack>
          )}
        />

        {error && <ReportErrorState title="Данные Proof-of-Play недоступны" message={error} onRetry={load} />}

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : data.length === 0 ? (
            <ReportEmptyState title="Нет подтверждений воспроизведения" message="За выбранный период записи Proof-of-Play не найдены." />
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Устройство</TableCell>
                    <TableCell>Медиа</TableCell>
                    <TableCell>Плейлист</TableCell>
                    <TableCell>Начало</TableCell>
                    <TableCell>Длительность</TableCell>
                    <TableCell>Завершенность</TableCell>
                    <TableCell>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.device_name || `#${row.device_id}`}</TableCell>
                      <TableCell>{row.media_title || (row.media_id ? `#${row.media_id}` : '-')}</TableCell>
                      <TableCell>{row.playlist_name || (row.playlist_id ? `#${row.playlist_id}` : '-')}</TableCell>
                      <TableCell>{new Date(row.started_at).toLocaleString()}</TableCell>
                      <TableCell>{row.duration_actual ?? row.duration_planned ?? 0}s</TableCell>
                      <TableCell>{(row.completion_rate ?? 0).toFixed(1)}%</TableCell>
                      <TableCell>
                        {row.completed ? 'Завершено' : row.interrupted ? 'Прервано' : 'В процессе'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[size]}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
              />
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ProofOfPlayPage;
